import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

// Renders the DS .row-menu-btn trigger (revealed on row hover) and, on click,
// a position:fixed .menu popover holding the page-provided action content.
export default function DataTableRowMenu(props) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  const toggle = (e) => {
    e.stopPropagation();
    setOpen((o) => !o);
  };

  // Position once the menu has mounted so we can measure its height: right-align the menu's
  // right edge under the button (grows leftward, no wide-content spill) and drop it DOWN,
  // flipping UP when there isn't room below (and there is above). useLayoutEffect runs before
  // paint, so the final position is applied with no visible flicker.
  useLayoutEffect(() => {
    if (!open) return;
    const btn = btnRef.current;
    const menu = menuRef.current;
    if (!btn || !menu) return;
    const r = btn.getBoundingClientRect();
    const menuH = menu.offsetHeight;
    const gap = 4;
    const roomBelow = window.innerHeight - r.bottom;
    const top =
      roomBelow >= menuH + gap || r.top < menuH + gap ? r.bottom + gap : r.top - menuH - gap;
    setPos({ top, right: Math.max(8, window.innerWidth - r.right) });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      close();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open, close]);

  return (
    <>
      <button
        ref={btnRef}
        type='button'
        className={`row-menu-btn${open ? ' open' : ''}`}
        onClick={toggle}
        aria-label='Row actions'
      >
        <span className='row-menu-dots' />
      </button>
      {open && (
        <div
          ref={menuRef}
          className='menu'
          style={{ top: pos.top, right: pos.right }}
          onClick={close}
        >
          {props.children}
        </div>
      )}
    </>
  );
}
